import { Facilitator, MarketInfo, Reserve } from '@/domain/market-info/marketInfo'

import { CapAutomatorInfo } from '@/domain/cap-automator/types'
import { MarketOverview } from '../types'
import { getReserveEModeCategoryTokens } from './getReserveEModeCategoryTokens'
import { getBorrowableAmount } from '@/utils/getBorrowableAmount'
import { USDXL_ADDRESS } from '@/config/consts'

export interface MakeMarketOverviewParams {
  marketInfo: MarketInfo
  reserve: Reserve
  capAutomatorInfo: CapAutomatorInfo
  facilitator: Facilitator
}

export function makeMarketOverview({
  reserve,
  marketInfo,
  capAutomatorInfo,
  facilitator,
}: MakeMarketOverviewParams): MarketOverview {
  const eModeCategoryId = reserve.eModeCategory?.id
  const eModeCategoryTokens = getReserveEModeCategoryTokens(marketInfo, reserve)

  const isUSDXL = reserve.token.address === USDXL_ADDRESS

  const available = getBorrowableAmount({
    tokenIdentifier: reserve.token.address,
    facilitatorAvailable: facilitator.availableToBorrow,
    defaultAvailable: reserve.availableLiquidity,
  })

  return {
    reserveStatus: reserve.status,
    supply: {
      status: reserve.supplyAvailabilityStatus,
      totalSupplied: reserve.totalLiquidity,
      supplyCap: reserve.supplyCap,
      apy: reserve.supplyAPY,
      capAutomatorInfo: capAutomatorInfo.supplyCap,
    },
    collateral: {
      status: reserve.collateralEligibilityStatus,
      token: reserve.token,
      maxLtv: reserve.maxLtv,
      liquidationThreshold: reserve.liquidationThreshold,
      liquidationPenalty: reserve.liquidationBonus,
      isolationModeInfo: { debt: reserve.isolationModeTotalDebt, debtCeiling: reserve.debtCeiling },
    },
    borrow: {
      status: reserve.borrowEligibilityStatus,
      totalBorrowed: reserve.totalDebt,
      borrowCap: reserve.borrowCap,
      apy: reserve.variableBorrowApy,
      reserveFactor: reserve.reserveFactor,
      chartProps: {
        utilizationRate: isUSDXL ? facilitator.utilizationRate : reserve.utilizationRate,
        optimalUtilizationRate: reserve.optimalUtilizationRate,
        variableRateSlope1: reserve.variableRateSlope1,
        variableRateSlope2: reserve.variableRateSlope2,
        baseVariableBorrowRate: reserve.baseVariableBorrowRate,
      },
      capAutomatorInfo: capAutomatorInfo.borrowCap,
    },
    summary: {
      type: 'default',
      marketSize: isUSDXL ? facilitator.borrowLimit : reserve.totalLiquidity,
      utilizationRate: isUSDXL ? facilitator.utilizationRate : reserve.utilizationRate,
      borrowed: isUSDXL ? facilitator.currentBorrowed : reserve.totalDebt,
      available: isUSDXL ? facilitator.availableToBorrow : available,
    },
    ...(eModeCategoryId === 1 || eModeCategoryId === 2
      ? {
          eMode: {
            maxLtv: reserve.eModeCategory!.ltv,
            liquidationThreshold: reserve.eModeCategory!.liquidationThreshold,
            liquidationPenalty: reserve.eModeCategory!.liquidationBonus,
            categoryId: eModeCategoryId,
            eModeCategoryTokens,
          },
        }
      : {}),
  }
}
